import { Form, Formik, FormikHelpers, getIn } from 'formik';
import { MouseEvent, ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import NeglectedOrDelinquentLEAInformationPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentLEAInformationPanel';
import NeglectedOrDelinquentStudentPopulationPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentStudentPopulationPanel';
import NeglectedOrDelinquentStudentDemographicsPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentStudentDemographicsPanel';
import NeglectedOrDelinquentStudentCharacteristicsPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentStudentCharacteristicsPanel';
import NeglectedOrDelinquentHighSchoolCourseCreditsPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentHighSchoolCourseCreditsPanel';
import NeglectedOrDelinquentHighSchoolDiplomaPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentHighSchoolDiplomaPanel';
import NeglectedOrDelinquentGEDPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentGEDPanel';
import NeglectedOrDelinquentPostSecondaryEducationPanel from '../../../../components/FormLayouts/NeglectedOrDelinquent/NeglectedOrDelinquentPostSecondaryEducationPanel';
import '../../../../components/ManualInputPage/ManualInputPage.css';
import Loader from '../../../../components/Loader/Loader';
import Modal from '../../../../components/Modal/Modal';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
    createNeglectedOrDelinquentInformation,
    fetchNeglectedOrDelinquentInformation,
    fetchNeglectedOrDelinquentInputOptionSets,
    updateNeglectedOrDelinquentInformation,
} from '../../../../redux/thunks/neglectedOrDelinquentThunks';
import { appRoutePaths } from '../../../../services/route.service';
import { DialogType } from '../../../../types/DialogTypes';
import { FeatureAccessPermissions } from '../../../../types/FeatureAccessPermissions';
import {
    buildNeglectedOrDelinquentInformationModel,
    NeglectedOrDelinquentInformationFormValues,
    toNeglectedOrDelinquentInformationFormValues,
} from '../../../../types/NeglectedOrDelinquentInformationModel';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import { hasSpecifiedFeatureAccess } from '../../../../utilities/userUtilities';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './NeglectedOrDelinquentInput.css';

const hasDraftErrors = (
    errors: object,
    values: NeglectedOrDelinquentInformationFormValues,
    touched: object,
    prefix = ''
): boolean => Object.entries(errors).some(([key, error]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if ([
        'raceEthnicityTotal', 'genderTotal', 'ageTotal', 'studentCharacteristicsLimit',
        'courseCreditsWhileLimit', 'courseCreditsExitLimit', 'diplomaTotalLimit',
        'gedEnrolledTotal', 'gedEarnedTotal',
        'postSecondaryWhileLimit', 'postSecondaryExitLimit',
    ].includes(path)) return false;
    if (typeof error === 'object' && error !== null) {
        return hasDraftErrors(error, values, touched, path);
    }
    return getIn(values, path) !== '' || Boolean(getIn(touched, path));
});

const NeglectedOrDelinquentInput = (): ReactElement => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { schoolYear = '' } = useParams<{ schoolYear: string }>();
    const district = useAppSelector(selectSelectedDistrict);
    const {
        information,
        inputOptionSets,
        informationStatus,
        inputOptionSetsStatus,
        schoolYears,
    } = useAppSelector((state) => state.neglectedOrDelinquent);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const canEdit = hasSpecifiedFeatureAccess(
        FeatureAccessPermissions.NEGLECTED_OR_DELINQUENT_ACCESS
    );
    const facilityTypesKey = Object.keys(inputOptionSets).find(
        (key) => key.toLowerCase() === 'facilitytypeoptions'
    );
    const facilityTypes = facilityTypesKey ? inputOptionSets[facilityTypesKey] : [];
    const isStateOperatedAgency = district.type === 'State Operated Agency';
    const validationSchema = createNeglectedOrDelinquentInformationValidationSchema(
        facilityTypes, isStateOperatedAgency
    );
    const year = Number(schoolYear);
    const schoolYearDisplay =
        schoolYears.find((option) => option.value === schoolYear)?.text ||
        (Number.isFinite(year) && year > 0 ? `${year - 1}-${year}` : schoolYear);
    const isLoading = [informationStatus, inputOptionSetsStatus].some(
        (status) => status === 'idle' || status === 'pending'
    );
    const leaNameDisplay =
        district.name && district.districtId
            ? `${district.name} (${district.districtId})`
            : district.name;

    const handleCancelConfirm = (): void => {
        navigate(appRoutePaths.NeglectedOrDelinquentList);
    };

    const saveDraft = async (
        values: NeglectedOrDelinquentInformationFormValues,
        { resetForm, setSubmitting }: Pick<
            FormikHelpers<NeglectedOrDelinquentInformationFormValues>,
            'resetForm' | 'setSubmitting'
        >
    ): Promise<void> => {
        setSubmitting(true);
        const model = buildNeglectedOrDelinquentInformationModel(
            values, district.districtId, year, information?.id || '',
            isStateOperatedAgency
        );
        try {
            const saved = await dispatch(
                model.id
                    ? updateNeglectedOrDelinquentInformation(model)
                    : createNeglectedOrDelinquentInformation(model)
            ).unwrap();
            resetForm({
                values: toNeglectedOrDelinquentInformationFormValues(saved),
            });
            dispatch(createNotification({
                severity: 'success',
                children: 'Neglected or Delinquent Saved.',
            }));
        } catch {
            return;
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const informationRequest = dispatch(
            fetchNeglectedOrDelinquentInformation(schoolYear)
        );
        const optionsRequest = dispatch(fetchNeglectedOrDelinquentInputOptionSets());
        return () => {
            informationRequest.abort();
            optionsRequest.abort();
        };
    }, [dispatch, schoolYear, district.districtId]);

    return (
        <DistrictSchoolInfoPage
            isLoading={false}
            pageClass={
                'manual-input ' +
                'data-input--district-school-info--neglected-or-delinquent--input'
            }
            backBarLocation={appRoutePaths.NeglectedOrDelinquentList}
        >
            <div className="nord-input-container">
                <Formik
                    initialValues={toNeglectedOrDelinquentInformationFormValues(
                        information
                    )}
                    enableReinitialize
                    validateOnMount
                    validationSchema={validationSchema}
                    onSubmit={saveDraft}
                >
                    {({
                        dirty, errors, touched, values, isSubmitting,
                        resetForm, setSubmitting, validateForm,
                    }) => {
                        const disabled =
                            !canEdit ||
                            !district.districtId ||
                            informationStatus !== 'succeeded' ||
                            inputOptionSetsStatus !== 'succeeded' ||
                            isSubmitting;
                        return (
                            <Form onSubmit={async (event) => {
                                event.preventDefault();
                                const currentErrors = await validateForm();
                                if (disabled || !dirty ||
                                    hasDraftErrors(
                                        currentErrors, values, touched
                                    )) return;
                                await saveDraft(values, { resetForm, setSubmitting });
                            }}>
                                <div className="heading-button-error-container">
                                    <div className="manual-button-container">
                                        <span
                                            className="subheading"
                                            data-testid="component-heading-text"
                                        >
                                            Neglected or Delinquent
                                        </span>
                                        <div className="button-group">
                                            <p className="required-label">
                                                All Fields are Required
                                            </p>
                                            <ActionButton
                                                classes={
                                                    'button no-wrap-text cancel-button'
                                                }
                                                dataTestId="cancel-button"
                                                tooltipText="Navigate to the last page"
                                                onClick={() => {
                                                    if (dirty) setShowCancelModal(true);
                                                    else handleCancelConfirm();
                                                }}
                                            >
                                                Cancel
                                            </ActionButton>
                                            <ActionButton
                                                dataTestId="submit-button"
                                                classes={
                                                    'button--secondary no-wrap-text ' +
                                                    'submit-button'
                                                }
                                                disabled={disabled || !dirty ||
                                                    hasDraftErrors(
                                                        errors, values, touched
                                                    )}
                                                loading={isSubmitting}
                                                tooltipText={
                                                    dirty
                                                        ? 'Save Neglected or Delinquent.'
                                                        : 'No changes have been made.'
                                                }
                                                onClick={async (
                                                    event: MouseEvent<HTMLButtonElement>
                                                ) => {
                                                    event.preventDefault();
                                                    const currentErrors =
                                                        await validateForm();
                                                    if (hasDraftErrors(
                                                        currentErrors, values, touched
                                                    )) return;
                                                    await saveDraft(values, {
                                                        resetForm, setSubmitting,
                                                    });
                                                }}
                                            >
                                                Save
                                            </ActionButton>
                                        </div>
                                    </div>
                                </div>
                                {isLoading ? (
                                    <Loader
                                        dataTestId="neglected-or-delinquent-input-loader"
                                        message="Loading..." />
                                ) : (
                                    <>
                                        <NeglectedOrDelinquentLEAInformationPanel
                                            leaNameDisplay={leaNameDisplay}
                                            schoolYearDisplay={schoolYearDisplay}
                                            facilityTypes={facilityTypes}
                                            disabled={disabled}
                                            isStateOperatedAgency={
                                                isStateOperatedAgency
                                            }
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentStudentPopulationPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentStudentDemographicsPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentStudentCharacteristicsPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentHighSchoolCourseCreditsPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentHighSchoolDiplomaPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentGEDPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                        <NeglectedOrDelinquentPostSecondaryEducationPanel
                                            disabled={disabled}
                                            validationSchema={validationSchema}
                                        />
                                    </>
                                )}
                                {showCancelModal && (
                                    <Modal
                                        type={DialogType.CONFIRM}
                                        open={showCancelModal}
                                        title="Are you sure?"
                                        onClose={() => setShowCancelModal(false)}
                                    >
                                        <div
                                            className="confirm-container"
                                            data-testid="confirmation-modal"
                                        >
                                            <p data-testid="cancel-message">
                                                Your changes have not been saved. Do you
                                                wish to continue?
                                            </p>
                                            <div className="button-row">
                                                <ActionButton
                                                    dataTestId="yes-button"
                                                    onClick={handleCancelConfirm}
                                                >
                                                    <span>Yes</span>
                                                </ActionButton>
                                                <ActionButton
                                                    classes="button--secondary"
                                                    dataTestId="no-button"
                                                    onClick={() =>
                                                        setShowCancelModal(false)
                                                    }
                                                >
                                                    <span>No</span>
                                                </ActionButton>
                                            </div>
                                        </div>
                                    </Modal>
                                )}
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </DistrictSchoolInfoPage>
    );
};

export default NeglectedOrDelinquentInput;
